import { runCreateFolderWorkflow } from "@/action-dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { FilesRailDropTarget } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailDropTarget";
import {
  useFilesRailWorkspaceFiles,
  useFilesRailWorkspaceRootItems,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useFilesRailWorkspaceFiles";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { RailSectionFolderRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSectionFolderRow";
import { RailSectionLoading } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSectionLoading";
import { RailZeroState } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailZeroState";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  useActiveFileRecordId,
  useOpenFileRecord,
} from "@/panes/file-record-detail";
import { FilePlus, Folder, FolderOpen, FolderPlus, Upload } from "lucide-react";
import { useCallback } from "react";

/**
 * The "Files" sub-section inside an expanded workspace row — the file-record tree
 * scoped to the workspace. Reuses the same hook + recursive tree as the dedicated
 * Files rail so behaviour stays uniform. Its header kebab carries the creation
 * actions (New folder / Upload files / New document) for parity with the Canvases
 * and Chats sections. Mounted only while the workspace row is open, so the query
 * and the file dialogs are lazy.
 */
export function WorkspacesRailFilesSection({
  workspaceId,
}: {
  workspaceId: string;
}) {
  const activeFileRecordId = useActiveFileRecordId();
  const openFileRecord = useOpenFileRecord();
  const menu = useRailRowContextMenu();

  const openNewFolder = useCallback(
    (currentPath: string) => {
      window.setTimeout(
        () => void runCreateFolderWorkflow({ workspaceId, currentPath }),
        0,
      );
    },
    [workspaceId],
  );

  const {
    fileRecords,
    fileInputRef,
    dialogs,
    actions,
    menuActions,
    uploadQueue,
    isPending: filesLoading,
    handleUploadClick,
    handleUploadToPath,
    handleFilesSelected,
    getOsDropHandler,
  } = useFilesRailWorkspaceFiles(workspaceId, { enabled: true });

  const handleNewDocumentAtPath = useCallback(
    (parentPath: string) => {
      uploadQueue.createDoc(parentPath, {
        onCreated: (record) => openFileRecord(record.id),
      });
    },
    [uploadQueue, openFileRecord],
  );

  const handleNewDocument = useCallback(
    () => handleNewDocumentAtPath(""),
    [handleNewDocumentAtPath],
  );

  const rootItems = useFilesRailWorkspaceRootItems({
    workspaceId,
    fileRecords,
    activeFileRecordId,
    onOpenFile: actions.openFile,
    menuActions,
    getOsDropHandler,
    onUploadToPath: handleUploadToPath,
    onCreateDocumentAtPath: handleNewDocumentAtPath,
    onCreateSubfolder: openNewFolder,
  });

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFilesSelected}
      />
      {/* The whole Files folder (header + content) is the files organizing
          surface (rail-workspace-folder, parentPath=""): drop a file here to
          move it to the files root; canvases/chats show "can't". Works when
          collapsed. Folders inside win on hover. */}
      <FilesRailDropTarget
        workspaceId={workspaceId}
        parentPath=""
        onOsDrop={getOsDropHandler("")}
      >
        <RailSectionFolderRow
          label="Files"
          icon={Folder}
          openIcon={FolderOpen}
          expansionKey={`ws:${workspaceId}:files`}
          onContextMenu={menu.onContextMenu}
          menu={
            <RailRowMenu {...railRowMenuProps(menu)} label="Files">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  window.setTimeout(() => openNewFolder(""), 0);
                }}
              >
                <FolderPlus />
                New folder
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  window.setTimeout(handleUploadClick, 0);
                }}
              >
                <Upload />
                Upload files
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  window.setTimeout(handleNewDocument, 0);
                }}
              >
                <FilePlus />
                New document
              </DropdownMenuItem>
            </RailRowMenu>
          }
        >
          {filesLoading ? (
            <RailSectionLoading label="files" />
          ) : rootItems.length === 0 ? (
            <RailZeroState
              icon={Folder}
              accent="teal"
              variant="passive"
              title="No files yet."
              description="Workspace files appear here."
            />
          ) : (
            rootItems
          )}
        </RailSectionFolderRow>
      </FilesRailDropTarget>
      {dialogs}
    </>
  );
}
